import { Truck, ArrowUpRight, ShieldCheck, Star, Package, Boxes, Sofa, Trash2, Warehouse, HandHelping } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SITE } from "@/config/site";

const MOVE_SERVICES = [
  { icon: Truck, label: "House and apartment removals" },
  { icon: Package, label: "Furniture delivery" },
  { icon: Sofa, label: "Furniture transport" },
  { icon: Trash2, label: "Rubbish and unwanted item removal" },
  { icon: Warehouse, label: "Storage moves" },
  { icon: HandHelping, label: "Moving assistance" },
];

export const TrustedTeam = () => {
  const reviewsUrl = SITE.goVans.reviewsUrl;
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="trusted-team-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Trust block */}
        <Reveal>
          <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/60 p-8 sm:p-10 lg:p-12">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
              <ShieldCheck size={14} /> Established local team
            </span>
            <h2 className="mt-4 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
              Trusted Local Service Team
            </h2>
            <div className="mt-4 max-w-3xl space-y-4 text-lg text-slate-600">
              <p>
                GoCleaning is operated by the same local team behind{" "}
                <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-800" data-testid="trusted-govans-inline">GoVans.ie</a>,
                providing reliable local services across Dublin and surrounding areas.
              </p>
              <p>
                Our GoVans customers have already trusted us for removals, furniture transport and moving
                services, and we bring the same focus on clear communication, reliability and customer
                service to GoCleaning.
              </p>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" data-testid="visit-govans-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-3.5 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">
                Visit GoVans.ie <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              {reviewsUrl ? (
                <a href={reviewsUrl} target="_blank" rel="noopener noreferrer" data-testid="govans-reviews-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-3.5 text-base font-bold text-slate-800 transition-all hover:border-emerald-600 hover:text-emerald-700">
                  <Star size={17} className="text-amber-500" /> View GoVans Customer Reviews
                </a>
              ) : (
                <span className="inline-flex flex-col">
                  <span data-testid="govans-reviews-placeholder"
                    className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-full border-2 border-dashed border-slate-300 px-7 py-3.5 text-base font-bold text-slate-400">
                    <Star size={17} /> View GoVans Customer Reviews
                  </span>
                  <span className="mt-1 text-xs font-semibold text-amber-600">Placeholder — add the GoVans Google reviews link in config (SITE.goVans.reviewsUrl).</span>
                </span>
              )}
            </div>
            <p className="mt-4 text-xs text-slate-400">These are GoVans customer reviews, shown for the wider local team — not GoCleaning reviews.</p>
          </div>
        </Reveal>

        {/* Secondary: move help */}
        <Reveal delay={0.08}>
          <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-10">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white"><Boxes size={20} /></span>
              <h3 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">Need help with your move too?</h3>
            </div>
            <p className="mt-4 max-w-3xl text-slate-600">
              Moving home often requires more than cleaning. Through{" "}
              <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-800">GoVans.ie</a>,
              customers can also arrange:
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {MOVE_SERVICES.map((m) => (
                <li key={m.label} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><m.icon size={17} /></span>
                  <span className="font-semibold text-slate-700">{m.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7">
              <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" data-testid="move-govans-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-slate-800">
                Visit GoVans.ie <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
